import React, { useContext } from "react";
import CourseTypeIcon from "../../../components/Icons/CourseTypeIcon";
import { Box, Typography } from "@mui/material";
import TextFilter from "../../../components/ReactTable/Filters/TextFilter";
import DateFilter from "../../../components/ReactTable/Filters/DateFilter";
import CourseFlags from "../../../components/Icons/CourseFlags";
import ProgressBar from "../../../components/ProgressBar/ProgressBar";
import ActivitiesCompleted from "../../../components/ActivitiesCompleted/ActivitiesCompleted";
import coursePic from "../../../assets/images/course_pic.png";
import { Tooltip } from "../../../components/Tooltip";
import { CourseCardDueDate } from "../../../components/CourseCard";
import CourseCardLocation from "../../../components/CourseCard/CourseCardLocation";
import CourseActionButton from "../../../components/Buttons/CourseActionButton";
import UserAssignmentContext from "../../../context/UserAssignmentContext";

const CourseTitleWithFlags = ({
  courseTitle,
  courseFlags,
  courseDescription
}) => {
  const title = (
    <Box display="flex" flex={1}>
      <Box mr={2}>
        <Typography>{courseTitle}</Typography>
      </Box>
      <CourseFlags flags={courseFlags} />
    </Box>
  );
  return courseDescription ? (
    <Tooltip
      title={
        <Typography variant="body2" color="textPrimary">
          {courseDescription}
        </Typography>
      }
    >
      {title}
    </Tooltip>
  ) : (
    title
  );
};

export const imageColumn = {
  id: "image",
  accessor: "image",
  disableSortBy: true,
  style: {
    width: 88
  },
  Cell: ({ row: { original } }) => (
    <img
      style={{ verticalAlign: "middle" }}
      alt={original.name}
      src={original.image || coursePic}
      height="56"
    />
  )
};

export const courseTypeColumn = t => {
  return {
    Header: t("table.columnHeader.Type"),
    id: "courseType",
    accessor: "courseType",
    style: {
      textAlign: "center",
      width: 64
    },
    Cell: ({ row: { original } }) => (
      <CourseTypeIcon type={original.courseType} />
    )
  };
};

export const courseProgressColumn = {
  id: "totalActivities",
  style: {
    minWidth: 220,
    width: 220
  },
  Cell: ({ row: { original } }) =>
    original.userAssignment && original.userAssignment.totalActivities > 0 ? (
      <Box display="flex" flexDirection="column">
        <ActivitiesCompleted
          totalSteps={original.userAssignment.totalActivities}
          completedSteps={original.userAssignment.numActivitiesCompleted}
        />
        <ProgressBar
          totalSteps={original.userAssignment.totalActivities}
          completedSteps={original.userAssignment.numActivitiesCompleted}
        />
      </Box>
    ) : null
};

export const courseActionColumn = onClick => {
  return {
    style: {
      textAlign: "center"
    },
    id: "userAssignment",
    Cell: ({ row: { original: trainingObject } }) => {
      const { isEnrolled, isStarted } = useContext(UserAssignmentContext);

      return (
        <Box minWidth={80}>
          <CourseActionButton
            isEnrolled={isEnrolled(trainingObject)}
            startDate={trainingObject.zonedStartDate}
            endDate={trainingObject.zonedEndDate}
            isAssigned={!!trainingObject.userAssignment}
            isStarted={isStarted(trainingObject.userAssignment)}
            courseType={trainingObject.courseType}
            onEnrollClick={() => onClick(trainingObject)}
            onResumeCourseClick={() => onClick(trainingObject)}
            onAddToCoursesClick={() => onClick(trainingObject)}
            isInstructorForCourse={trainingObject.isInstructorForCourse}
            prereqs={trainingObject.prerequisites || []}
            hasNoFreeSeats={trainingObject.noFreeSeats}
            iltClassType={trainingObject.typeName}
          />
        </Box>
      );
    }
  };
};
export const titleColumn = t => {
  return {
    Header: t("table.columnHeader.title"),
    id: "name",
    accessor: "name",
    style: {
      minWidth: 276
    },
    Cell: ({ row: { original } }) => (
      <CourseTitleWithFlags
        courseDescription={original.description}
        courseTitle={original.name}
        courseFlags={original.flags}
      />
    )
  };
};

export const dueDateColumn = t => {
  return {
    Header: t("table.columnHeader.DueDate"),
    id: "courseDueDate",
    accessor: "courseDueDate",
    Filter: DateFilter,
    style: {
      minWidth: 132
    }
  };
};

export const iltDateColumn = t => {
  return {
    Header: t("table.columnHeader.dateAndTime"),
    id: "scheduledDate",
    accessor: "scheduledDate",
    Filter: DateFilter,
    style: {
      minWidth: 184
    },
    Cell: ({ row: { original } }) => (
      <CourseCardDueDate
        scheduledDate={original.scheduledDate}
        scheduledTime={original.scheduledTime}
        showCalendarIcon={false}
      />
    )
  };
};

export const iltLocationColumn = t => {
  return {
    Header: t("table.columnHeader.location"),
    id: "location",
    accessor: "location",
    Filter: TextFilter,
    style: {
      minWidth: 184
    },
    Cell: ({ row: { original } }) => {
      const location = original.iltInfo?.trainingLocation
        ? original.iltInfo.trainingLocation
        : null;
      return (
        <CourseCardLocation location={location} showLocationIcon={false} />
      );
    }
  };
};

export const policyNumberColumn = t => {
  return {
    Header: t("table.columnHeader.number"),
    id: "number",
    accessor: "number",
    style: {
      textAlign: "center",
      minWidth: 132
    }
  };
};

export const manualNameColumn = (t, isIframed) => {
  return {
    Header: isIframed
      ? t("table.columnHeader.coursePackageTitleIframed")
      : t("table.columnHeader.coursePackageTitle"),
    id: "manualName",
    accessor: "manualName",
    Cell: ({ cell: { value } }) => value?.join(", ") || "",
    style: {
      minWidth: 132
    }
  };
};
